/**
 * A full-length channel's lane under its desk row (Dax, 2026-09-29): the music's, the voiceover's or the
 * soundtrack's segments seen through the whole cut, one after another (2026-10-04). Each is its file drawn
 * from where it starts; dragging one moves it along the cut (which passage of a song plays, or a breath
 * before a take), a press selects it, and its handles then trim the stretch that plays. The strip is the
 * ASMR row's, and its waveforms are read from the audio the live mix already decoded, fine enough to place
 * a long file under a short cut.
 */
import { useEffect, useState } from 'react';
import { CutStrip, type StripSegment, type TrimEdge } from '@/components/cut-strip';
import type { PreviewMix } from '@/lib/preview-mix';

/** Bars across one cut's width: enough for the widest desk. */
const BARS_PER_WINDOW = 480;

/** One segment: its whole file, where the file starts on the cut, and the stretch of it that plays. */
export interface LanePiece { file: { url: string; fromMs: number; durationMs: number; peaks: number[] }; atMs: number; fromMs: number; toMs: number }

export function LayerLane({ mix, label, pieces, windowMs, muted, selected, onSelect, onDrag, onTrim }: {
  mix: PreviewMix;
  label: string;
  /** In the channel's order; a piece's key is its index. */
  pieces: (LanePiece | null)[];
  /** The cut's length: the window the lane shows. */
  windowMs: number;
  muted: boolean;
  selected: number | null;
  onSelect: (index: number) => void;
  onDrag: (index: number, deltaMs: number, done: boolean) => void;
  onTrim: (index: number, edge: TrimEdge, deltaMs: number, done: boolean) => void;
}) {
  const [fine, setFine] = useState<Record<string, number[]>>({});
  const files = [...new Map(pieces.filter((p): p is LanePiece => p !== null).map((p) => [`${p.file.url}|${p.file.fromMs}|${p.file.durationMs}`, p.file])).entries()];
  const filesKey = files.map(([k]) => k).join(' ');
  useEffect(() => {
    let live = true;
    for (const [key, file] of files) {
      if (fine[key]) continue;
      const points = Math.ceil((file.durationMs / Math.max(1, windowMs)) * BARS_PER_WINDOW);
      void mix.peaks(file.url, file.fromMs, file.durationMs, points).then((p) => { if (live && p) setFine((f) => ({ ...f, [key]: p })); });
    }
    return () => { live = false; };
  }, [mix, filesKey, windowMs]);
  const segments: StripSegment[] = pieces.flatMap((p, i) => (p ? [{
    key: i,
    atMs: p.atMs,
    durationMs: p.file.durationMs,
    peaks: fine[`${p.file.url}|${p.file.fromMs}|${p.file.durationMs}`] ?? p.file.peaks,
    heardFromMs: p.fromMs,
    heardToMs: p.toMs,
    off: muted,
    holdFromMs: p.atMs + p.fromMs,
    holdToMs: p.atMs + p.toMs,
  }] : []));
  return <CutStrip segments={segments} totalMs={windowMs} cuts={[]} label={label} selected={selected} onSelect={onSelect} onDrag={onDrag} onTrim={onTrim} />;
}
